import { inject, ref } from 'vue';
import type { Ref } from 'vue';

import type {
    Resource,
    Client,
    Company,
    User,
} from '@keyvue/one-api-client';
import type { Company as LegacyCompany, User as LegacyUser } from '@keyvue/one-api-client-legacy';

export interface ReactiveCollection<T extends Resource> {
    load: (params?: { [key: string]: any }) => Promise<void>;
    data: Ref<T[]>;
    hasData: Ref<boolean>;
    params: Ref<object>;
    count: Ref<number>;
    total: Ref<number>;
    loading: Ref<boolean>;
    error: Ref<any>;
}

export default function useClient() {
    const client = inject<Client>('client');

    if (!client) {
        throw new Error('api plugin not enabled');
    }

    const legacyCompany = inject<Ref<LegacyCompany>>('legacyCompany');
    const legacyUser = inject<Ref<LegacyUser>>('legacyUser');

    function createCollection<T extends Resource>(
        collectionParams: object,
    ): ReactiveCollection<T> {
        return {
            load: async (params?: { [key: string]: any }) => { throw new Error(JSON.stringify(params)); },
            params: ref<object>(collectionParams),
            data: ref<T[]>([]) as Ref<T[]>,
            hasData: ref<boolean>(false),
            count: ref<number>(0),
            total: ref<number>(0),
            loading: ref<boolean>(false),
            error: ref<any>(null),
        };
    }

    function parseResponse<T extends Resource>(
        collection: ReactiveCollection<T>,
        response: { data: Array<T>, total: number },
    ): ReactiveCollection<T> {
        const localCollection = collection;

        try {
            localCollection.error.value = null;
            localCollection.data.value.splice(0);
            localCollection.data.value = response.data;
            localCollection.hasData.value = response.data.length > 0;
            localCollection.count.value = response.data.length;
            localCollection.total.value = response.total;
            localCollection.loading.value = false;
        } catch (error) {
            localCollection.error.value = error;
        } finally {
            localCollection.loading.value = false;
        }

        return localCollection;
    }

    function getLazyResourceCollection<T extends Resource>(
        Class: new (...args: any[]) => T,
        params: object = {},
    ): ReactiveCollection<T> {
        const collection = createCollection<T>(params);

        collection.load = async (updatedParams?: { [key: string]: any }) => {
            collection.loading.value = true;
            collection.params.value = updatedParams ?? collection.params.value;

            if (!client) {
                throw new Error('api plugin not enabled');
            }

            const response = await client.getCollection<T>(Class, collection.params.value);

            parseResponse<T>(collection, response);
        };

        return collection;
    }

    function getLazySubResourceCollection<SubResourceIdentifier, S extends Resource>(
        parentResource: Resource,
        SubResource: new (...args: any[]) => S,
        subResourceIdentifier: SubResourceIdentifier,
        params?: any,
    ): ReactiveCollection<S> {
        const collection = createCollection<S>(params);

        collection.load = async (updatedParams?: { [key: string]: any }) => {
            collection.loading.value = true;
            const loadParams = updatedParams ?? collection.params.value;

            if (!client) {
                throw new Error('api plugin not enabled');
            }

            const response = await client.getSubResources(
                parentResource,
                SubResource,
                subResourceIdentifier,
                loadParams,
            );

            parseResponse<S>(collection, response);
        };

        return collection;
    }

    async function getResourceCollection<T extends Resource>(
        Class: new (...args: any[]) => T,
        params: object = {},
    ): Promise<ReactiveCollection<T>> {
        const collection = getLazyResourceCollection<T>(
            Class,
            params,
        );

        await collection.load();

        return collection;
    }

    async function getSubResourceCollection<SubResourceIdentifier, S extends Resource>(
        parentResource: Resource,
        SubResource: new (...args: any[]) => S,
        subResourceIdentifier: SubResourceIdentifier,
        params?: any,
    ): Promise<ReactiveCollection<S>> {
        const collection = getLazySubResourceCollection(
            parentResource,
            SubResource,
            subResourceIdentifier,
            params,
        );

        await collection.load();

        return collection;
    }

    return {
        client,
        currentUser: ref(client.session.currentUser) as Ref<User>,
        currentCompany: ref(client.session.currentCompany) as Ref<Company>,
        currentLegacyUser: legacyUser as Ref<LegacyUser> ?? ref(null),
        currentLegacyCompany: legacyCompany as Ref<LegacyCompany> ?? ref(null),
        getLazyResourceCollection,
        getLazySubResourceCollection,
        getResourceCollection,
        getSubResourceCollection,
    };
}
